/**
 * OrderValueBreakdownDialog — shared breakdown modal for the two order-value
 * KPIs surfaced on Overview (and previously on the Orders dashboard):
 *   - "Total Active Order Value"     (`variant="active"`)
 *   - "Open Uninvoiced Order Value"  (`variant="uninvoiced"`)
 *
 * Renders the same DKK / EUR / count summary + sortable per-order table
 * (order · status · invoice state · value · gross margin · reason).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { OrderStatusBadge } from './StatusBadge';
import {
    FileText,
    AlertCircle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
} from 'lucide-react';

const STATE_LABEL = {
    booked: { text: 'Booked', cls: 'bg-emerald-100 text-emerald-800' },
    economic_draft: { text: 'E-conomic Draft', cls: 'bg-blue-100 text-blue-800' },
    local_draft: { text: 'Local Draft', cls: 'bg-slate-100 text-slate-700' },
    none: { text: 'Uninvoiced', cls: 'bg-amber-100 text-amber-800' },
};

const formatEur0 = (n) =>
    (n ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const OrderValueBreakdownDialog = ({ open, onOpenChange, metric, variant }) => {
    const navigate = useNavigate();
    const [sort, setSort] = useState({ field: 'dkk', dir: 'desc' });

    const isActive = variant === 'active';
    const title = isActive
        ? 'Total Active Order Value — Breakdown'
        : 'Open Uninvoiced Order Value — Breakdown';
    const desc = isActive
        ? 'All non-draft, non-cancelled orders — full production pipeline value including work that already has a sales document or booked invoice.'
        : 'Orders that still need to be invoiced. Excludes anything with a local draft sales document, an e-conomic draft invoice, or a booked invoice (those live under Finance).';
    const accentBg = isActive ? 'bg-indigo-50 border-indigo-200' : 'bg-amber-50 border-amber-200';
    const accentLine = isActive ? 'border-indigo-200' : 'border-amber-200';
    const accentIcon = isActive
        ? <FileText className="h-5 w-5 text-indigo-600" />
        : <AlertCircle className="h-5 w-5 text-amber-600" />;

    const toggleSort = (field) => {
        setSort((prev) => {
            if (prev.field !== field) return { field, dir: 'desc' };
            return { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
        });
    };

    const valueFor = (row) => {
        if (sort.field === 'gross_profit') return row.gross_profit_eur;
        if (sort.field === 'margin') return row.gross_margin_pct;
        return row.dkk;
    };

    const details = metric?.details || [];
    const sorted = [...details].sort((a, b) => {
        const av = valueFor(a);
        const bv = valueFor(b);
        const aNull = av == null;
        const bNull = bv == null;
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;
        return sort.dir === 'desc' ? (bv - av) : (av - bv);
    });

    const renderSortIcon = (field) => {
        if (sort.field !== field) {
            return <ArrowUpDown className="inline h-3 w-3 ml-1 text-slate-300" />;
        }
        return sort.dir === 'desc'
            ? <ArrowDown className="inline h-3 w-3 ml-1 text-slate-500" />
            : <ArrowUp className="inline h-3 w-3 ml-1 text-slate-500" />;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {accentIcon}
                        {title}
                    </DialogTitle>
                    <DialogDescription>{desc}</DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto -mx-6 px-6">
                    <div className={`mb-3 flex items-center gap-4 text-sm ${accentBg} border rounded-lg p-3`}>
                        <div>
                            <div className="text-xs text-slate-500">Total DKK</div>
                            <div className="text-base font-semibold text-slate-900" data-testid="breakdown-total-dkk">
                                {formatEur0(metric?.dkk)} DKK
                            </div>
                        </div>
                        <div className={`border-l ${accentLine} pl-4`}>
                            <div className="text-xs text-slate-500">Total EUR</div>
                            <div className="text-base font-semibold text-slate-900" data-testid="breakdown-total-eur">
                                {formatEur0(metric?.eur)} EUR
                            </div>
                        </div>
                        <div className={`border-l ${accentLine} pl-4`}>
                            <div className="text-xs text-slate-500">Orders</div>
                            <div className="text-base font-semibold text-slate-900" data-testid="breakdown-count">{metric?.count ?? 0}</div>
                        </div>
                    </div>

                    {details.length === 0 ? (
                        <div className="py-10 text-center text-sm text-slate-500">
                            {isActive ? 'No active orders right now.' : 'Nothing left to invoice — everything with a sales document or booked invoice.'}
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b">
                                <tr>
                                    <th className="text-left py-2 font-medium">Order</th>
                                    <th className="text-left py-2 font-medium">Status</th>
                                    <th className="text-left py-2 font-medium">Invoice</th>
                                    <th
                                        className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                        onClick={() => toggleSort('dkk')}
                                        data-testid="breakdown-sort-dkk"
                                    >
                                        DKK {renderSortIcon('dkk')}
                                    </th>
                                    <th className="text-right py-2 font-medium">EUR</th>
                                    <th
                                        className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                        onClick={() => toggleSort('gross_profit')}
                                        data-testid="breakdown-sort-gross-profit"
                                    >
                                        Gross Margin {renderSortIcon('gross_profit')}
                                    </th>
                                    <th
                                        className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700 pl-1"
                                        onClick={() => toggleSort('margin')}
                                        data-testid="breakdown-sort-margin-pct"
                                    >
                                        % {renderSortIcon('margin')}
                                    </th>
                                    <th className="text-left py-2 font-medium pl-4">Reason</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {sorted.map((d) => {
                                    const lbl = STATE_LABEL[d.invoice_state] || STATE_LABEL.none;
                                    const marginPct = d.gross_margin_pct;
                                    const profitEur = d.gross_profit_eur;
                                    let marginCls = 'text-slate-400';
                                    if (marginPct != null) {
                                        if (marginPct < 20) marginCls = 'text-rose-600 font-medium';
                                        else if (marginPct < 35) marginCls = 'text-amber-600';
                                        else marginCls = 'text-emerald-700 font-medium';
                                    }
                                    let profitCls = 'text-slate-400';
                                    if (profitEur != null) {
                                        profitCls = profitEur < 0 ? 'text-rose-600 font-medium' : 'text-slate-900';
                                    }
                                    return (
                                        <tr
                                            key={d.id}
                                            className="hover:bg-slate-50 cursor-pointer"
                                            onClick={() => { onOpenChange(false); navigate(`/orders/${d.id}`); }}
                                            data-testid={`breakdown-row-${d.order_number}`}
                                        >
                                            <td className="py-2">
                                                <div className="font-medium text-slate-900">#{String(d.order_number).padStart(6, '0')}</div>
                                                <div className="text-xs text-slate-500 truncate max-w-[260px]">{d.title}</div>
                                            </td>
                                            <td className="py-2">
                                                <OrderStatusBadge status={d.status} />
                                            </td>
                                            <td className="py-2">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${lbl.cls}`}>
                                                    {lbl.text}
                                                </span>
                                            </td>
                                            <td className="py-2 text-right tabular-nums">{formatEur0(d.dkk)}</td>
                                            <td className="py-2 text-right tabular-nums text-slate-500">{formatEur0(d.eur)}</td>
                                            <td
                                                className={`py-2 text-right tabular-nums ${profitEur == null ? '' : profitCls}`}
                                                data-testid={`breakdown-row-${d.order_number}-gross-profit`}
                                            >
                                                {profitEur != null ? (
                                                    `€${formatEur0(profitEur)}`
                                                ) : (
                                                    <span
                                                        className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                                                        title={d.gross_margin_reason || 'No margin data available'}
                                                    >
                                                        {d.gross_margin_reason || '—'}
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                className={`py-2 text-right tabular-nums pl-1 ${marginPct == null ? 'text-slate-400' : marginCls}`}
                                                data-testid={`breakdown-row-${d.order_number}-margin-pct`}
                                            >
                                                {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
                                            </td>
                                            <td className="py-2 pl-4 text-xs text-slate-500 max-w-[240px]">
                                                {d.inclusion_reason}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default OrderValueBreakdownDialog;
